import { Button } from 'blaze-ui/Button/Button'
import Styles from './MobileStop.module.scss'
import { Email } from 'blaze-ui/icons/24'
import { LinkAngled } from 'blaze-ui/icons'
import { generatePath, useHistory } from 'react-router-dom'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { ReactNode } from 'react'
import { copyTextToClipboard } from 'utils/articleUtils'
import { UserClient } from 'api/clients/UserClient'
import { useAPMobileWeb } from 'hooks/useAPMobileWeb'
import { SCHEDULED_POSTS_PATH } from 'paths'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { LegacyMobileStop } from './LegacyMobileStop/LegacyMobileStop'
import { FREE_REWARD_TIERS } from 'hooks/useAutopilotLimits'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useVisualStylesForExistingUsers } from 'hooks/useVisualStylesForExistingUsers/useVisualStylesForExistingUsers'

export type Props = {
  onNavigate?: () => void
  variant?:
    | 'home'
    | 'invite-team-members'
    | 'referrals'
    | 'content-plan'
    | 'approvals'
    | 'paid-ads'
    | 'trial-ended'
    | 'trial-ended-freemium'
    | 'credits'
}

export const MobileStop = (props: Props) => {
  const { variant = 'home', onNavigate } = props
  const imageUrl = useMobileStopGraphic(variant)
  const title = useMobileStopTitle(variant)
  const description = useMobileStopDescription(variant)

  const workspaceId = useCurrentWorkspaceId()
  const isAPMobileWeb = useAPMobileWeb()

  useVisualStylesForExistingUsers()

  const history = useHistory()

  const handleCopyDesktopLink = () => {
    // eslint-disable-next-line no-restricted-properties
    copyTextToClipboard(window.location.href)
    showToastMessage('Link copied to the clipboard!', 'success')
  }

  const handleBack = () => {
    onNavigate?.()
    history.push(generatePath(SCHEDULED_POSTS_PATH, { workspaceId }))
  }

  const handleEmail = () => {
    // eslint-disable-next-line no-restricted-properties
    UserClient.sendMobileAutopilotDesktopLinkEmail(window.location.href)
    showToastMessage('Email sent!')
  }

  const hideBack = variant === 'trial-ended-freemium'
  const backLabel = variant === 'trial-ended' || variant === 'credits' ? 'Back' : 'Take me to calendar'

  if (!isAPMobileWeb) {
    return <LegacyMobileStop />
  }

  return (
    <div className={Styles.root}>
      <div className={Styles.textContainer}>
        <Heading level={1}>{title}</Heading>
        <Text>{description}</Text>
      </div>
      <div className={Styles.buttonGroup}>
        <Button className={Styles.button} size="xl" frontIcon={Email} onPress={handleEmail}>
          Email Me the Desktop Link
        </Button>
        <Button
          className={Styles.button}
          size="xl"
          variant="tertiary"
          frontIcon={LinkAngled}
          onPress={handleCopyDesktopLink}
        >
          Copy Link
        </Button>
        {hideBack ? null : (
          <Button className={Styles.button} size="xl" variant="editor" onPress={handleBack}>
            {backLabel}
          </Button>
        )}
      </div>
      {imageUrl ? <img className={Styles.image} src={imageUrl} alt="Mobile Stop Graphic" /> : null}
    </div>
  )
}

const useMobileStopGraphic = (variant: Props['variant']): string | undefined => {
  switch (variant) {
    case 'home':
    case 'trial-ended':
    case 'trial-ended-freemium':
    case 'credits':
      return 'https://res.cloudinary.com/almanac/image/upload/v1764083483/blaze_assets/mobile/mobile_web_blocker_home_nfzxzh.png'
    case 'approvals':
      return 'https://res.cloudinary.com/almanac/image/upload/v1764085211/blaze_assets/mobile/mobile_web_blocker_approvals_tiw7eg.png'
    case 'content-plan':
      return 'https://res.cloudinary.com/almanac/image/upload/v1764084972/blaze_assets/mobile/mobile_web_blocker_content_plan_l0ygiz.png'
    case 'paid-ads':
      return 'https://res.cloudinary.com/almanac/image/upload/v1764085319/blaze_assets/mobile/mobile_web_blocker_paid_ads_iqakim.png'
    default:
      return undefined
  }
}

const useMobileStopTitle = (variant: Props['variant']): string => {
  switch (variant) {
    case 'approvals':
      return 'Approvals is a premium feature'
    case 'invite-team-members':
      return 'Inviting team members is a premium feature'
    case 'paid-ads':
      return 'Paid Ads is a premium feature'
    case 'trial-ended':
      return 'Your trial has ended. Upgrade now to keep creating.'
    case 'trial-ended-freemium':
      return 'Your trial has ended. Upgrade to resume your full schedule.'
    case 'credits':
      return 'Need more content credits?'
    case 'home':
    case 'referrals':
    default:
      return 'Autopilot works best on desktop'
  }
}

const useMobileStopDescription = (variant: Props['variant']): ReactNode => {
  switch (variant) {
    case 'invite-team-members':
    case 'approvals':
    case 'paid-ads':
      return 'You can upgrade on desktop at app.blaze.ai'
    case 'trial-ended':
      return 'You can still view and edit your existing content anytime. Upgrade on desktop at app.blaze.ai to create new content.'
    case 'credits':
      return 'Upgrade to a paid plan for up to 1200 monthly credits, or buy 100 add-on credits for $10 at app.blaze.ai.'
    case 'trial-ended-freemium':
      return (
        <>
          You’re now on the <strong>Free</strong> plan—your schedules are limited to{' '}
          <strong>{FREE_REWARD_TIERS[0]} posts/week</strong> across <strong>3 channels</strong>. Go to app.blaze.ai on
          your computer to start the free experience.
        </>
      )
    case 'content-plan':
      return (
        <>
          Content plan is coming to mobile soon.
          <br />
          Please use Autopilot on desktop at app.blaze.ai.
        </>
      )
    case 'referrals':
      return (
        <>
          Referrals are coming to mobile soon.
          <br />
          Please use Autopilot on desktop at app.blaze.ai.
        </>
      )
    case 'home':
    default:
      return (
        <>
          Go to <strong>app.blaze.ai</strong> on your computer for the best experience.
        </>
      )
  }
}
